import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from '@h5p-platform/i18n/locales/en/common.json';
import enAdmin from '@h5p-platform/i18n/locales/en/admin.json';
import enErrors from '@h5p-platform/i18n/locales/en/errors.json';
import viCommon from '@h5p-platform/i18n/locales/vi/common.json';
import viAdmin from '@h5p-platform/i18n/locales/vi/admin.json';
import viErrors from '@h5p-platform/i18n/locales/vi/errors.json';

/**
 * Khởi tạo i18next cho web-admin — plan §4.4.
 *
 * Locale GỐC là `en`, locale đích là `vi`. Ngược đời với một sản phẩm dùng ở
 * Việt Nam, nhưng có lý do: key và fallback tiếng Anh giữ cho chuỗi tiếng Việt
 * không lẫn vào mã nguồn, và locale thứ ba sau này chỉ cần thêm một thư mục.
 *
 * Locale được NHÚNG vào bundle chứ không tải qua HTTP. Ba namespace × hai ngôn
 * ngữ chỉ vài chục KB, còn tải động thì màn hình đầu tiên chớp qua một nhịp
 * không có chữ — cái giá lớn hơn nhiều so với vài KB.
 */
export const SUPPORTED_LOCALES = ['vi', 'en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = 'h5p-admin-locale';

/**
 * Pseudo-locale kiểm layout — plan §4.7. Chỉ có khi chạy với
 * `VITE_PSEUDO_LOCALE=true` (xem `npm run dev:admin:pseudo`).
 *
 * Bật bằng biến build chứ không phải cờ lúc chạy: locale này KHÔNG được lọt
 * vào bundle production. Vite loại hẳn nhánh dưới khi biến là `false`, nên
 * cả file JSON lẫn nhánh code đều biến mất khỏi bundle thật.
 */
export const PSEUDO_LOCALE = 'en-XA';
const pseudoEnabled = import.meta.env.VITE_PSEUDO_LOCALE === 'true';

export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Locale khởi động.
 *
 * Thứ tự: lựa chọn đã lưu → ngôn ngữ trình duyệt → `vi`. Lựa chọn của người
 * dùng phải thắng ngôn ngữ trình duyệt, nếu không thì mỗi lần đổi máy lại phải
 * chọn lại. Phase 6 sẽ đồng bộ giá trị này vào hồ sơ người dùng (§5.1).
 */
export function detectLocale(): SupportedLocale {
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && isSupportedLocale(saved)) return saved;

  // `navigator.language` là BCP-47 đầy đủ (`vi-VN`), cắt lấy phần ngôn ngữ.
  const browser = navigator.language.split('-')[0] ?? '';
  return isSupportedLocale(browser) ? browser : 'vi';
}

export function persistLocale(locale: SupportedLocale): void {
  window.localStorage.setItem(STORAGE_KEY, locale);
  // Cập nhật thuộc tính lang của tài liệu: trình đọc màn hình chọn giọng theo
  // nó, và CSS `:lang()` dựa vào nó để chọn font.
  document.documentElement.lang = locale;
}

/**
 * Nạp pseudo-locale nếu được bật.
 *
 * File do `npm run i18n:pseudo` sinh ra và KHÔNG được commit, nên import tĩnh
 * sẽ làm build hỏng ở máy chưa chạy generator. `import.meta.glob` cho phép
 * thiếu file mà vẫn biên dịch được.
 */
async function loadPseudoResources(): Promise<Record<string, object> | undefined> {
  if (!pseudoEnabled) return undefined;

  const modules = import.meta.glob<{ default: object }>(
    '../../../../packages/i18n/locales/en-XA/*.json',
  );
  const entries = await Promise.all(
    Object.entries(modules).map(async ([path, load]) => {
      const namespace = path.split('/').pop()?.replace('.json', '') ?? '';
      return [namespace, (await load()).default] as const;
    }),
  );

  if (entries.length === 0) {
    console.warn(
      `Chưa có locale ${PSEUDO_LOCALE}. Chạy "npm run i18n:pseudo" để sinh, rồi khởi động lại.`,
    );
    return undefined;
  }
  return Object.fromEntries(entries);
}

export async function initI18n(): Promise<void> {
  const pseudo = await loadPseudoResources();
  // Pseudo-locale thắng lựa chọn đã lưu: bật cờ lên là để soi layout, không
  // phải để rơi về locale lần trước người dùng chọn.
  const locale = pseudo ? PSEUDO_LOCALE : detectLocale();

  await i18next.use(initReactI18next).init({
    lng: locale,
    // Pseudo-locale KHÔNG fallback sang en: chuỗi thiếu phải hiện ra key trần
    // để nhìn thấy, chứ lặng lẽ hiện tiếng Anh bình thường thì mất luôn mục
    // đích của việc soi.
    fallbackLng: pseudo ? false : 'en',
    ns: ['common', 'admin', 'errors'],
    defaultNS: 'common',
    resources: {
      en: { common: enCommon, admin: enAdmin, errors: enErrors },
      vi: { common: viCommon, admin: viAdmin, errors: viErrors },
      ...(pseudo ? { [PSEUDO_LOCALE]: pseudo } : {}),
    },
    interpolation: {
      // React đã chống XSS khi render; escape lần nữa làm dấu nháy tiếng Việt
      // hiện thành `&#39;` trên màn hình.
      escapeValue: false,
    },
    // Thấy key trần trên màn hình là tín hiệu rõ ràng rằng bản dịch còn thiếu.
    // Trả chuỗi rỗng sẽ giấu lỗi cho tới khi khách phát hiện.
    returnEmptyString: false,
  });

  document.documentElement.lang = locale;
}

export default i18next;
